import React, { useEffect, useState, useRef } from 'react'
import { collection, query, where, onSnapshot, updateDoc, doc, deleteDoc, writeBatch } from 'firebase/firestore'
import { sendPasswordResetEmail, deleteUser } from 'firebase/auth'
import { db, auth, callCreateUser, callCreateUsersBulk } from '../../firebase.config'
import { useAuth, PLAN_LIMITS } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

const ROLES = ['admin', 'supervisor', 'foreman', 'superintendant', 'member']

const ROLE_COLORS: Record<string, string> = { admin: 'bg-red-100 text-red-700', supervisor: 'bg-blue-100 text-blue-700', foreman: 'bg-amber-100 text-amber-700', superintendant: 'bg-purple-100 text-purple-700', member: 'bg-green-100 text-green-700' }
const STATUS_COLORS: Record<string, string> = { approved: 'bg-green-100 text-green-700', pending: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700' }

// Track local edits before committing
interface EditState {
  crewId: string
  reportsTo: string
  role: string
  assignedProjectId: string
}

export default function UsersPage() {
  const { orgId, userData, orgData, projects: orgProjects } = useAuth()
  const [users, setUsers] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState('member')
  const [newAssignedProjectId, setNewAssignedProjectId] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  // Bulk import state
  const [showImport, setShowImport] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResults, setImportResults] = useState<{ success: number; errors: string[] } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [orgUsers, setOrgUsers] = useState<any[]>([])

  const planLimit = PLAN_LIMITS[orgData?.plan || 'core'] || 4
  const atLimit = users.length >= planLimit

  // Pending edits per user (admin only)
  const [edits, setEdits] = useState<Record<string, EditState>>({})
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'users'), where('orgId', '==', orgId)),
      snap => { setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) },
      (err) => console.error('Users snapshot error:', err)
    )
    return () => unsub()
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'users'), where('orgId', '==', orgId)),
      snap => setOrgUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error('OrgUsers snapshot error:', err)
    )
    return () => unsub()
  }, [orgId])

  const handleApprove = async (id: string) => {
    await updateDoc(doc(db, 'organizations', orgId, 'users', id), { status: 'approved', updatedAt: new Date().toISOString() })
  }

  const handleReject = async (id: string) => {
    await updateDoc(doc(db, 'organizations', orgId, 'users', id), { status: 'rejected', updatedAt: new Date().toISOString() })
  }

  const handleDelete = async (id: string) => {
    // Read orgId from sessionStorage to avoid stale closure state
    let currentOrgId = orgId
    try {
      const raw = sessionStorage.getItem('sitedeck_auth')
      if (raw) {
        const data = JSON.parse(raw)
        if (data?.orgId) currentOrgId = data.orgId
      }
    } catch { /* ignore */ }

    if (!currentOrgId) {
      setCreateError('Cannot delete: org not resolved. Please log out and back in as admin.')
      return
    }
    if (!window.confirm('Are you sure you want to remove this user? This cannot be undone.')) return
    try {
      await deleteDoc(doc(db, 'organizations', currentOrgId, 'users', id))
    } catch (err: any) {
      console.error('Delete user error:', err)
      setCreateError('Delete failed: ' + (err.message || 'permission denied'))
    }
  }

  // Track a local edit
  const trackEdit = (userId: string, field: keyof EditState, value: string) => {
    setEdits(prev => ({
      ...prev,
      [userId]: { ...prev[userId], [field]: value }
    }))
  }

  // Check if a user has pending edits
  const hasEdits = (userId: string, original: any) => {
    const e = edits[userId]
    if (!e) return false
    return (
      (e.crewId !== undefined && e.crewId !== (original.crewId || '')) ||
      (e.reportsTo !== undefined && e.reportsTo !== (original.reportsTo || '')) ||
      (e.role !== undefined && e.role !== (original.role || '')) ||
      (e.assignedProjectId !== undefined && e.assignedProjectId !== (original.assignedProjectId || ''))
    )
  }

  // Save all pending edits
  const handleSaveAll = async () => {
    if (!orgId || !userData) return

    // Capture admin's orgId and uid in case auth state changes mid-function
    const adminOrgId = orgId
    const adminUid = userData.uid
    const adminName = userData.name

    setSaving(true)
    const changedUsers = Object.keys(edits).filter(id => hasEdits(id, users.find(u => u.id === id)))
    const now = new Date().toISOString()
    const batch = writeBatch(db)
    const auditLogs: any[] = []

    for (const userId of changedUsers) {
      const original = users.find(u => u.id === userId)
      const e = edits[userId]
      if (!original || !e) continue

      const updates: Record<string, string> = { updatedAt: now }
      const changes: string[] = []

      if (e.crewId !== undefined && e.crewId !== (original.crewId || '')) {
        updates.crewId = e.crewId
        changes.push(`crewId: "${original.crewId || ''}" → "${e.crewId}"`)
      }
      if (e.reportsTo !== undefined && e.reportsTo !== (original.reportsTo || '')) {
        updates.reportsTo = e.reportsTo
        const oldName = orgUsers.find(u => u.id === original.reportsTo)?.name || original.reportsTo || 'none'
        const newName = orgUsers.find(u => u.id === e.reportsTo)?.name || e.reportsTo || 'none'
        changes.push(`reportsTo: "${oldName}" → "${newName}"`)
      }
      if (e.role !== undefined && e.role !== (original.role || '')) {
        updates.role = e.role
        changes.push(`role: "${original.role}" → "${e.role}"`)
      }
      if (e.assignedProjectId !== undefined && e.assignedProjectId !== (original.assignedProjectId || '')) {
        updates.assignedProjectId = e.assignedProjectId
        const oldProject = orgProjects.find(p => p.id === original.assignedProjectId)?.name || original.assignedProjectId || 'none'
        const newProject = orgProjects.find(p => p.id === e.assignedProjectId)?.name || e.assignedProjectId || 'none'
        changes.push(`project: "${oldProject}" → "${newProject}"`)
      }

      batch.update(doc(db, 'organizations', adminOrgId, 'users', userId), updates)

      auditLogs.push({
        action: 'user_update',
        changedByUid: adminUid,
        changedByName: adminName,
        targetUserId: userId,
        targetUserName: original.name,
        changes,
        orgId: adminOrgId,
        createdAt: now,
      })
    }

    await batch.commit()

    // Write audit logs
    if (auditLogs.length > 0) {
      const auditBatch = writeBatch(db)
      auditLogs.forEach(log => {
        auditBatch.set(doc(collection(db, 'organizations', orgId, 'auditLogs')), log)
      })
      await auditBatch.commit()
    }

    setEdits({})
    setSaving(false)
    setSavedMsg(`Saved ${changedUsers.length} change${changedUsers.length !== 1 ? 's' : ''}`)
    setTimeout(() => setSavedMsg(''), 3000)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId) return
    if (atLimit) {
      setCreateError(`Your ${orgData?.plan || 'core'} plan supports up to ${planLimit} users. Upgrade to add more.`)
      return
    }

    setCreating(true)
    setCreateError('')
    try {
      const result: any = await callCreateUser({
        email: newEmail,
        name: newName,
        role: newRole,
        targetOrgId: orgId,
      })

      if (result?.result?.success) {
        // If a project was selected, assign the new user to it
        if (newAssignedProjectId && result?.result?.uid) {
          try {
            await updateDoc(doc(db, 'organizations', orgId, 'users', result.result.uid), {
              assignedProjectId: newAssignedProjectId,
              updatedAt: new Date().toISOString()
            })
          } catch (err: any) {
            console.error('Failed to assign new user to project:', err)
          }
        }
        setShowCreate(false)
        setNewName('')
        setNewEmail('')
        setNewRole('member')
        setNewAssignedProjectId('')
      } else {
        setCreateError(result?.result?.error?.message || result?.message || 'Failed to create user')
      }
    } catch (err: any) {
      const message = err?.message || 'Failed to create user'
      setCreateError(message)
    } finally {
      setCreating(false)
    }
  }

  const downloadTemplate = () => {
    const header = 'name,email,role,crewId'
    const rows = [
      header,
      'John Smith,john.smith@example.com,member,Crew A',
      'Jane Doe,jane.doe@example.com,foreman,Crew A',
      'Bob Johnson,bob.johnson@example.com,supervisor,Crew A',
    ]
    const csv = rows.join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'user_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const parseCSV = (text: string): { name: string; email: string; role: string; crewId: string }[] => {
    const lines = text.trim().split('\n')
    if (lines.length < 2) return []
    const header = lines[0].toLowerCase().split(',').map(h => h.trim())
    const nameIdx = header.indexOf('name')
    const emailIdx = header.indexOf('email')
    const roleIdx = header.indexOf('role')
    const crewIdx = header.indexOf('crewid')
    if (nameIdx === -1 || emailIdx === -1) return []

    return lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim())
      return {
        name: cols[nameIdx] || '',
        email: cols[emailIdx] || '',
        role: roleIdx !== -1 ? (cols[roleIdx] || 'member').toLowerCase() : 'member',
        crewId: crewIdx !== -1 ? cols[crewIdx] || '' : '',
      }
    }).filter(r => r.email)
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !orgId) return

    const text = await file.text()
    const rows = parseCSV(text)

    if (rows.length === 0) {
      setImportResults({ success: 0, errors: ['No valid users found. Make sure your CSV has name, email, and role columns.'] })
      return
    }

    if (users.length + rows.length > planLimit) {
      setImportResults({ success: 0, errors: [`Your ${orgData?.plan || 'core'} plan supports up to ${planLimit} users. This import would exceed that limit.`] })
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setImporting(true)
    setImportResults(null)

    try {
      const users = rows.map(row => ({
        email: row.email,
        name: row.name,
        role: ROLES.includes(row.role) ? row.role : 'member',
      }))

      const result: any = await callCreateUsersBulk(users, orgId)

      if (result?.result) {
        const { success, failed, errors } = result.result
        setImportResults({ success, errors: errors || [] })
      } else {
        setImportResults({ success: 0, errors: ['Import failed. Please try again.'] })
      }
    } catch (err: any) {
      setImportResults({ success: 0, errors: [err?.message || 'Import failed'] })
    }

    setImporting(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const filtered = users.filter(u =>
    !search || (u.name && u.name.toLowerCase().includes(search.toLowerCase())) || (u.email && u.email.toLowerCase().includes(search.toLowerCase()))
  )
  const pending = filtered.filter(u => u.status === 'pending')
  const others = filtered.filter(u => u.status !== 'pending')

  const isAdmin = userData?.role === 'admin'
  const hasPendingEdits = Object.keys(edits).some(id => hasEdits(id, users.find(u => u.id === id)))

  return (
    <AppLayout title="Users">
      <div className="mb-6 flex items-center gap-3">
        <input type="text" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64" />
        {isAdmin && (
          <>
            <button onClick={() => setShowCreate(true)} disabled={atLimit}
              className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
              + Add User
            </button>
            <button onClick={() => { setShowImport(true); setImportResults(null) }} disabled={atLimit}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed">
              Import Users
            </button>
          </>
        )}
        <span className={`text-xs font-medium px-3 py-1 rounded-full ${atLimit ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-100 text-gray-600'}`}>
          {users.length} / {planLimit} users
        </span>
      </div>
      {atLimit && isAdmin && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-sm text-red-700">
          Your {orgData?.plan || 'core'} plan supports up to {planLimit} users. <button onClick={() => window.location.href = '/settings'} className="underline font-medium">Upgrade plan</button> to add more.
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-yellow-600 mb-3">Pending Approval ({pending.length})</h3>
          <div className="bg-yellow-50 rounded-xl border border-yellow-200 overflow-hidden">
            {pending.map(u => (
              <div key={u.id} className="flex items-center justify-between px-5 py-4 border-b border-yellow-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-yellow-500 flex items-center justify-center text-white font-semibold text-sm">
                    {(u.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{u.name || 'Unknown'}</p>
                    <p className="text-sm text-gray-500">{u.email}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => handleApprove(u.id)} className="text-sm text-green-600 hover:underline font-medium">Approve</button>
                  <button onClick={() => handleReject(u.id)} className="text-sm text-red-600 hover:underline font-medium">Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save bar (admin only, shown when edits exist) */}
      {isAdmin && hasPendingEdits && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center justify-between">
          <span className="text-sm text-blue-700">You have unsaved changes.</span>
          <div className="flex gap-3">
            <button onClick={() => setEdits({})}
              className="text-sm text-gray-600 hover:text-gray-800 font-medium">Discard</button>
            <button onClick={handleSaveAll} disabled={saving}
              className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:bg-amber-300">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {savedMsg && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-sm text-green-700">
          ✓ {savedMsg}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Name</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Email</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Role</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
              {isAdmin && <th className="text-left px-5 py-3 font-medium text-gray-600">Change Role</th>}
              {isAdmin && <th className="text-left px-5 py-3 font-medium text-gray-600">Project</th>}
              {isAdmin && <th className="text-left px-5 py-3 font-medium text-gray-600">Crew</th>}
              {isAdmin && <th className="text-left px-5 py-3 font-medium text-gray-600">Reports To</th>}
              <th className="text-left px-5 py-3 font-medium text-gray-600"></th>
            </tr>
          </thead>
          <tbody>
            {others.map(u => (
              <tr key={u.id} className="border-b border-gray-100">
                <td className="px-5 py-3 font-medium text-gray-900">{u.name || 'Unknown'}</td>
                <td className="px-5 py-3 text-gray-600">{u.email}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || ROLE_COLORS.member}`}>
                    {u.role || 'member'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[u.status] || STATUS_COLORS.pending}`}>
                    {u.status || 'pending'}
                  </span>
                </td>
                {isAdmin && (
                  <>
                    <td className="px-5 py-3">
                      <select
                        value={edits[u.id]?.role ?? u.role ?? 'member'}
                        onChange={e => trackEdit(u.id, 'role', e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs">
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-3">
                      <select
                        value={edits[u.id]?.assignedProjectId ?? u.assignedProjectId ?? ''}
                        onChange={e => trackEdit(u.id, 'assignedProjectId', e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs bg-white max-w-40"
                      >
                        <option value="">— Unassigned —</option>
                        {orgProjects.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3">
                      <input
                        type="text"
                        value={edits[u.id]?.crewId ?? u.crewId ?? ''}
                        onChange={e => trackEdit(u.id, 'crewId', e.target.value)}
                        placeholder="e.g. Crew A"
                        className="border border-gray-300 rounded px-2 py-1 text-xs w-24"
                      />
                    </td>
                    <td className="px-5 py-3">
                      <select
                        value={edits[u.id]?.reportsTo ?? u.reportsTo ?? ''}
                        onChange={e => trackEdit(u.id, 'reportsTo', e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs bg-white max-w-32">
                        <option value="">— None —</option>
                        {orgUsers.filter(su => ['admin', 'supervisor', 'foreman', 'superintendant'].includes(su.role || '') && su.id !== u.id).map(su => (
                          <option key={su.id} value={su.id}>{su.name || su.email}</option>
                        ))}
                      </select>
                    </td>
                  </>
                )}
                <td className="px-5 py-3">
                  <button onClick={() => handleDelete(u.id)} className="text-sm text-red-600 hover:underline font-medium">Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Add New User</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            {createError && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{createError}</div>
            )}
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={newRole} onChange={e => setNewRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                  {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                <select value={newAssignedProjectId} onChange={e => setNewAssignedProjectId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                  <option value="">— Select project (optional) —</option>
                  {orgProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
                A password reset email will be sent to the user. They&apos;ll be required to set their own password on first login.
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={creating}
                  className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Bulk Import Users</h2>
              <button onClick={() => { setShowImport(false); setImportResults(null) }} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-3">
                <button onClick={downloadTemplate}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Download Template
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" id="csv-upload" />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <div className="text-gray-500 text-sm mb-2">Upload your completed CSV file</div>
                  <div className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 inline-block">
                    {importing ? 'Importing...' : 'Select CSV File'}
                  </div>
                </label>
              </div>

              {importResults && (
                <div className={`rounded-lg p-4 text-sm ${importResults.errors.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <p className={`font-medium mb-1 ${importResults.errors.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {importResults.errors.length > 0 ? `Imported ${importResults.success} users with ${importResults.errors.length} errors:` : `Successfully imported ${importResults.success} users!`}
                  </p>
                  {importResults.errors.length > 0 && (
                    <ul className="text-red-600 mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {importResults.errors.map((e, i) => <li key={i} className="text-xs">• {e}</li>)}
                    </ul>
                  )}
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs text-gray-600">
                <p className="font-medium mb-1">CSV Format:</p>
                <p>Columns: <code className="bg-gray-200 px-1 rounded">name</code>, <code className="bg-gray-200 px-1 rounded">email</code>, <code className="bg-gray-200 px-1 rounded">role</code>, <code className="bg-gray-200 px-1 rounded">crewId</code> (optional)</p>
                <p className="mt-1">Roles: admin, supervisor, foreman, superintendant, member</p>
                <p className="mt-1">Each user will receive a password reset email to set their own password.</p>
              </div>

              <div className="flex justify-end">
                <button onClick={() => { setShowImport(false); setImportResults(null) }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
